import {
  type ListProjectsQuery,
  type Porte,
  PORTE_LABELS,
  PROJECT_STATUS_LABELS,
  type ProjectStatus,
  porteSchema,
  projectStatusSchema,
  setorSlugSchema,
  type SetorSlug,
  type Uf,
  ufSchema,
} from '@idpe/schema';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { Badge } from '~/components/ui/Badge.js';
import { Button } from '~/components/ui/Button.js';
import {
  DataTable,
  PaginationControls,
  type SortState,
  useDataTableSort,
} from '~/components/ui/DataTable.js';
import { Field } from '~/components/ui/Field.js';
import { Select } from '~/components/ui/Select.js';
import { useProjectsList } from '~/features/projects/hooks.js';
import { useSectorsList } from '~/features/taxonomies/hooks.js';

const STATUS_TONE: Record<ProjectStatus, 'primary' | 'success' | 'warning' | 'neutral' | 'muted'> =
  {
    estruturacao: 'neutral',
    'consulta-publica': 'warning',
    licitacao: 'warning',
    'contrato-assinado': 'success',
    operacao: 'success',
    revisao: 'primary',
  };

const searchSchema = z.object({
  q: z.string().optional(),
  setor: setorSlugSchema.optional(),
  status: projectStatusSchema.optional(),
  porte: porteSchema.optional(),
  uf: ufSchema.optional(),
  page: z.coerce.number().int().min(1).default(1).optional(),
  perPage: z.coerce.number().int().min(1).max(100).default(20).optional(),
  sort: z.string().optional(),
});

export const Route = createFileRoute('/_auth/projects/')({
  validateSearch: (search) => searchSchema.parse(search),
  component: ProjectsListPage,
});

function ProjectsListPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [qInput, setQInput] = useState(search.q ?? '');
  const [sort, setSort] = useDataTableSort(
    search.sort
      ? (() => {
          const [campo, dir] = search.sort.split(':');
          return campo
            ? ({ campo, dir: (dir === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc' } as SortState)
            : undefined;
        })()
      : undefined,
  );

  const query: Partial<ListProjectsQuery> = {
    q: search.q,
    setor: search.setor,
    status: search.status,
    porte: search.porte,
    uf: search.uf,
    page: search.page ?? 1,
    perPage: search.perPage ?? 20,
    sort: sort ? `${sort.campo}:${sort.dir}` : undefined,
  };

  const { data, isLoading, error, refetch } = useProjectsList(query);
  const sectors = useSectorsList();

  function updateSearch(patch: Partial<typeof search>) {
    navigate({
      to: '/projects',
      search: { ...search, ...patch, page: patch.page ?? 1 },
    });
  }

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ O site</p>
        <h1 className="page-title">Projetos</h1>
        <p className="page-sub">
          O portfólio do instituto. Marque "Exibir no mapa" para o projeto ganhar um pino na página
          "IDPE em Números", e "Exibir na home" para ele entrar nos cases em destaque da página
          inicial.
        </p>
      </header>

      <section>
        <div className="toolbar">
          <div className="toolbar__row">
            <div className="toolbar__filters">
              <Field
                label="Buscar"
                placeholder="nome ou município"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') updateSearch({ q: qInput || undefined });
                }}
                onBlur={() => updateSearch({ q: qInput || undefined })}
              />
              <Select
                label="Setor"
                value={search.setor ?? ''}
                onChange={(e) =>
                  updateSearch({ setor: (e.target.value as SetorSlug) || undefined })
                }
                options={[
                  { value: '', label: 'Todos os setores' },
                  ...(sectors.data ?? []).map((s) => ({ value: s.slug, label: s.nome })),
                ]}
              />
              <Select
                label="Status"
                value={search.status ?? ''}
                onChange={(e) =>
                  updateSearch({ status: (e.target.value as ProjectStatus) || undefined })
                }
                options={[
                  { value: '', label: 'Todos' },
                  ...Object.entries(PROJECT_STATUS_LABELS).map(([v, l]) => ({
                    value: v,
                    label: l,
                  })),
                ]}
              />
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Link to="/projects/new" style={{ width: '100%' }}>
                  <Button variant="primary" fullWidth>
                    Novo projeto
                  </Button>
                </Link>
              </div>
            </div>
            <div
              className="toolbar__filters"
              style={{ gridTemplateColumns: '1fr 1fr', maxWidth: 480 }}
            >
              <Select
                label="Porte"
                value={search.porte ?? ''}
                onChange={(e) => updateSearch({ porte: (e.target.value as Porte) || undefined })}
                options={[
                  { value: '', label: 'Todos' },
                  ...Object.entries(PORTE_LABELS).map(([v, l]) => ({ value: v, label: l })),
                ]}
              />
              <Select
                label="UF"
                value={search.uf ?? ''}
                onChange={(e) => updateSearch({ uf: (e.target.value as Uf) || undefined })}
                options={[
                  { value: '', label: 'Todas' },
                  ...ufSchema.options.map((uf) => ({ value: uf, label: uf })),
                ]}
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: 'var(--s-5)' }}>
          <DataTable
            data={data?.data ?? []}
            loading={isLoading}
            error={error ? { message: error.displayMessage, retry: () => void refetch() } : null}
            keyExtractor={(p) => p.id}
            sort={sort}
            onSortChange={(s) => {
              setSort(s);
              updateSearch({ sort: s ? `${s.campo}:${s.dir}` : undefined });
            }}
            onRowClick={(p) => navigate({ to: '/projects/$slug', params: { slug: p.slug } })}
            emptyMessage="Nenhum projeto encontrado com esses filtros."
            emptyAction={
              <Link to="/projects/new">
                <Button variant="secondary">Criar primeiro projeto</Button>
              </Link>
            }
            columns={[
              {
                key: 'nome',
                header: 'Nome',
                sortable: true,
                mobileLine: 'primary',
                cell: (p) => <strong>{p.nome}</strong>,
              },
              {
                key: 'municipio',
                header: 'Local',
                sortable: true,
                mobileLine: 'secondary',
                cell: (p) => (
                  <span>
                    {p.municipio} / {p.estadoUf}
                  </span>
                ),
              },
              {
                key: 'setorSlug',
                header: 'Setor',
                mobileLine: 'meta',
                cell: (p) => {
                  const s = sectors.data?.find((sec) => sec.slug === p.setorSlug);
                  return s ? <Badge tone="primary">{s.nome}</Badge> : p.setorSlug;
                },
              },
              {
                key: 'status',
                header: 'Status',
                mobileLine: 'meta',
                cell: (p) => (
                  <Badge tone={STATUS_TONE[p.status]}>{PROJECT_STATUS_LABELS[p.status]}</Badge>
                ),
              },
              {
                key: 'capex',
                header: 'CAPEX',
                sortable: true,
                align: 'right',
                hideOnMobile: true,
                mobileLine: 'meta',
                cell: (p) => (p.capexCentavos ? formatCapex(p.capexCentavos) : '—'),
              },
              {
                key: 'ano',
                header: 'Ano',
                sortable: true,
                align: 'right',
                hideOnMobile: true,
                mobileLine: 'hidden',
                cell: (p) => p.ano ?? '—',
              },
              {
                key: 'flags',
                header: 'Exibir',
                mobileLine: 'hidden',
                cell: (p) => (
                  <span style={{ display: 'inline-flex', gap: 'var(--s-1)' }}>
                    {p.exibirHome && <Badge tone="success">home</Badge>}
                    {p.exibirMapa && <Badge tone="neutral">mapa</Badge>}
                  </span>
                ),
              },
            ]}
          />
        </div>

        {data && data.total > 0 && (
          <PaginationControls
            page={data.page}
            perPage={data.perPage}
            total={data.total}
            totalPages={data.totalPages}
            onPageChange={(page) => updateSearch({ page })}
          />
        )}
      </section>
    </div>
  );
}

function formatCapex(centavos: number): string {
  const reais = centavos / 100;
  if (reais >= 1_000_000_000) return `R$ ${(reais / 1_000_000_000).toFixed(1)} bi`;
  if (reais >= 1_000_000) return `R$ ${(reais / 1_000_000).toFixed(0)} mi`;
  if (reais >= 1_000) return `R$ ${(reais / 1_000).toFixed(0)} mil`;
  return `R$ ${reais.toFixed(0)}`;
}
